import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

/**
 * OUI 1.19 (OpenSearch fork) ships no `EuiProvider`/`colorMode`/Emotion theme
 * engine, so light vs. dark is switched the legacy way: by enabling exactly one
 * of the two theme stylesheets loaded in `public/index.html` (ids
 * `oui-theme-light` / `oui-theme-dark`). This context owns that switch,
 * persists the choice, and defaults to the OS `prefers-color-scheme`.
 *
 * A pre-paint inline script in index.html applies the initial mode to avoid a
 * flash of the wrong theme; this provider re-applies on mount and on change.
 */

export type ColorMode = "light" | "dark";

const STORAGE_KEY = "anketa.colorMode";

interface ColorModeContextType {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextType | undefined>(
  undefined,
);

const prefersDark = (): boolean =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const readStoredMode = (): ColorMode | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
};

/** Enable exactly one theme stylesheet. Safe to call before the links exist. */
export const applyColorMode = (mode: ColorMode): void => {
  const light = document.getElementById(
    "oui-theme-light",
  ) as HTMLLinkElement | null;
  const dark = document.getElementById(
    "oui-theme-dark",
  ) as HTMLLinkElement | null;
  if (light) light.disabled = mode === "dark";
  if (dark) dark.disabled = mode !== "dark";
  document.documentElement.setAttribute("data-color-mode", mode);
};

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [colorMode, setColorModeState] = useState<ColorMode>(
    () => readStoredMode() ?? (prefersDark() ? "dark" : "light"),
  );

  const setColorMode = (mode: ColorMode) => {
    setColorModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore quota / privacy-mode failures */
    }
  };

  const toggleColorMode = () =>
    setColorMode(colorMode === "dark" ? "light" : "dark");

  // Apply the active mode to the stylesheets whenever it changes.
  useEffect(() => {
    applyColorMode(colorMode);
  }, [colorMode]);

  // Follow OS changes only while the user has expressed no explicit preference.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      if (readStoredMode() === null) {
        setColorModeState(e.matches ? "dark" : "light");
      }
    };
    // Safari < 14 only supports the deprecated addListener signature.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return (
    <ColorModeContext.Provider
      value={{ colorMode, setColorMode, toggleColorMode }}
    >
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = (): ColorModeContextType => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
};

/**
 * Pins the theme to a fixed mode while mounted, restoring the user's chosen
 * mode on unmount. Used by the marketing homepage, which is light-only (its
 * sections use a fixed hex palette rather than OUI theme tokens).
 */
export const ForceColorMode: React.FC<{
  mode: ColorMode;
  children?: React.ReactNode;
}> = ({ mode, children }) => {
  const { colorMode } = useColorMode();
  useEffect(() => {
    applyColorMode(mode);
    return () => applyColorMode(colorMode);
  }, [mode, colorMode]);
  return <>{children ?? null}</>;
};
